import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  ToggleButton,
  useTheme,
  ListItemText,
  List,
  Divider,
  ListItemButton,
  useMediaQuery,
  Drawer,
  Toolbar,
  IconButton,
} from "@mui/material";
import Header from "../../common/Header";
import Card from "../../common/Card";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { tokens } from "../../../theme";
import Button from "../../common/Button";
import { Close, MenuOutlined, SaveOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  fetchAllUserRoles,
  fetchUserPermissions,
  PermissionSectionDTO,
  saveUserPermissions,
  UserRole,
  useUserReducer,
} from "../../../redux/slice/userSlice";
import {
  selectIsValueFoundInToken,
  selectTokenValues,
  useAuthSlice,
} from "../../../redux/slice/authSlice";
import { CAN_ACCESS_APPLICATION, CAN_ACCESS_USER_PERMISSION } from "../../../constants/applicationConstants";
import {
  setSingleAlertObj,
  SingleAlertInfo,
} from "../../../redux/slice/commonSlice";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { getV3CommissionTypeDisplayLabel } from "../../../utils/Utility";

interface PermissionItem {
  code: string;
  label: string;
  enabled: boolean;
}

type RoleGroupKey = "ADMIN" | "PRACTICE" | "AGENCY" | "MANUFACTURER";

const groupKeyFor = (r: UserRole): RoleGroupKey => {
  const roleName = (r.roleName || "").toUpperCase();
  const entity = (r.entityName || "").toUpperCase();

  if (
    roleName === "ROLE_ADMIN" ||
    roleName === "ROLE_SUPERADMIN" ||
    entity === "ADMIN"
  ) {
    return "ADMIN";
  }
  if (entity === "PRACTICE" || entity === "BILLER" || entity === "PROVIDER") {
    return "PRACTICE";
  }
  if (entity === "AGENCY" || entity === "ACCOUNTEXECUTIVE") {
    return "AGENCY";
  }
  return "MANUFACTURER";
};

const sortWithinGroup = (a: UserRole, b: UserRole) => {
  const an = (a.roleName || "").toUpperCase();
  const bn = (b.roleName || "").toUpperCase();

  const rank = (n: string) =>
    n === "ROLE_SUPERADMIN" ? 0 : n === "ROLE_ADMIN" ? 1 : 2;
  const ra = rank(an);
  const rb = rank(bn);
  if (ra !== rb) return ra - rb;

  return (a.roleDisplayValue || a.roleName || "").localeCompare(
    b.roleDisplayValue || b.roleName || ""
  );
};

interface PermissionSectionProps {
  section: PermissionSectionDTO;
  toggleHandler: (sectionName: string, code: string) => void;
  colors: any;
  colorKey: string;
  canEdit: boolean;
  onToggleAll: (sectionName: string, value: boolean) => void;
}
const PermissionSection: React.FC<PermissionSectionProps> = ({
  section,
  toggleHandler,
  colors,
  colorKey,
  canEdit,
  onToggleAll,
}) => {
  const itemsToRender = section.items;

  const items = section.items ?? [];
  const single = items.length === 1 ? items[0] : null;

  const isSimpleToggle =
    !!single &&
    (single.label?.trim().toLowerCase() === "enabled" ||
    single.label?.trim().toLowerCase() === "disabled");

  const simpleOn = !!single?.enabled;


  const total = section.items?.length ?? 0;
  const onCount = section.items?.filter((i) => i.enabled).length ?? 0;


  return (
    <Card
        role={canEdit && isSimpleToggle ? "button" : undefined}
        aria-pressed={isSimpleToggle ? simpleOn : undefined}
        onClick={
          canEdit && isSimpleToggle
            ? () => toggleHandler(section.tokenName, items[0].code)
            : undefined
        }
        sx={{
          p: 2,
          mb: 2,
          border: isSimpleToggle ? items[0].enabled
            ? `2px solid ${colors[colorKey][400]}`
            : "2px solid #4b4b4b" : "none",
           boxShadow: isSimpleToggle && items[0].enabled
            ? "0 0 8px rgba(0,255,255,0.3)"
            : ""
        }}
      >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {section.header}
        </Typography>

        {canEdit && total > 0 && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              color="success"
              variant="outlined"
              onClick={() => onToggleAll(section.tokenName, true)}
            >
              ALL ON
            </Button>
            <Button
              size="small"
              color="error"
              variant="outlined"
              onClick={() => onToggleAll(section.tokenName, false)}
            >
              ALL OFF
            </Button>
          </Box>
        )}
      </Box>

      {!!section.description && (
        <Typography sx={{ mb: 2 }} color="text.secondary">
          {section.description} {isSimpleToggle ? "": !!total && `( ${onCount}/${total} on )`}
        </Typography>
      )}

      {!itemsToRender ||
      itemsToRender.length === 0 ||
      (itemsToRender[0].code === "" && itemsToRender[0].label === "") ? (
        <Typography variant="body2" color="text.secondary">
          {canEdit ? "No values configured for this permission." : ""}
        </Typography>
      ) : (
        isSimpleToggle ?
        (
          <Box sx={{ height: 6 }} />
        ): (
        <Grid container spacing={2}>
          {itemsToRender.map((permission: PermissionItem) =>(
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={`${section.tokenName}:${permission.code}`}
            >
              <Card
                sx={{
                  width: "100%",
                  border: permission.enabled
                    ? `2px solid ${colors[colorKey][400]}`
                    : "2px solid #4b4b4b",
                  backgroundColor: permission.enabled
                    ? `${colors[colorKey][800]}`
                    : "transparent",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  padding: "16px",
                  boxShadow: permission.enabled
                    ? "0 0 8px rgba(0, 255, 255, 0.3)"
                    : "0px 0px 5px rgba(0, 0, 0, 0.2)",
                  transition:
                    "background-color 0.2s ease, box-shadow 0.2s ease",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    {permission.label}
                  </Typography>
                </Box>

                {canEdit && (
                  <ToggleButton
                    value="check"
                    selected={permission.enabled}
                    onChange={() =>
                      toggleHandler(section.tokenName, permission.code)
                    }
                    sx={{
                      ml: 2,
                      color: permission.enabled
                        ? colors[colorKey][400]
                        : "#999",
                      width: 80,
                      "&.Mui-selected": {
                        color: colors.grey[300],
                        backgroundColor: colors[colorKey][400],
                      },
                    }}
                  >
                    {permission.enabled ? (
                      <CheckCircleIcon sx={{ mr: 1 }} />
                    ) : (
                      <CancelIcon sx={{ mr: 1 }} />
                    )}
                    {permission.enabled ? "On" : "Off"}
                  </ToggleButton>
                )}
              </Card>
            </Grid>
          )
          )}
        </Grid>
        ))}
    </Card>
  );
};

const UserPermissions: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const groupLabels = useMemo(
    (): Record<RoleGroupKey, string> => ({
      ADMIN: getV3CommissionTypeDisplayLabel(allCodeTablesValuesMap),
      PRACTICE: "Practice",
      AGENCY: "Agency",
      MANUFACTURER: "Manufacturer",
    }),
    [allCodeTablesValuesMap]
  );

  const { permissionsByUserType, fetchPermissionsByUserTypeStatus, userRoles } =
    useUserReducer();

  const [activeRole, setActiveRole] = useState<UserRole | null>(null);
  const [roleDrawerOpen, setRoleDrawerOpen] = useState(false);
  const [localPermissions, setLocalPermissions] = useState<
    PermissionSectionDTO[]
  >([]);

  const [originalPermissions, setOriginalPermissions] = useState<
    PermissionSectionDTO[]
  >([]);
  const [isChangeTokenValue, setIsChangeTokenValue] = useState(false);
  const { userInfo } = useAuthSlice();

  const managingUserRoles = useSelector((state: RootState) =>
    selectTokenValues("canManageUsers")(state)
  );
  const canAccessUserPermissionEdit = useSelector((state: RootState) =>
    selectIsValueFoundInToken(
      "canAccessUserPermission",
      CAN_ACCESS_USER_PERMISSION.EDIT
    )(state)
  );

  const canAccessUsersList = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.USER_MANAGEMENT)(state));

  useEffect(() => {
    dispatch(fetchAllUserRoles());
  }, [dispatch]);

  const availableUserRoles = useMemo(() => {
    if (!userRoles) return [];

    // If user cannot access users list, only show their own role
    if (!canAccessUsersList && userInfo?.userRole) {
      const userRole = userRoles.find(
        (role: UserRole) => role.roleName === userInfo.userRole
      );
      return userRole ? [userRole] : [];
    }

    // Otherwise, show all manageable roles
    const filtered = userRoles.filter((role: UserRole) =>
      managingUserRoles.includes(role.roleName)
    );

    const isSuper = (r: UserRole) =>
      (r.roleDisplayValue || "").toLowerCase() === "super admin";

    return [
      ...filtered.filter(isSuper),
      ...filtered.filter((r) => !isSuper(r)),
    ];
  }, [userRoles, managingUserRoles, canAccessUsersList, userInfo]);
  
  const groups = useMemo(() => {
    const map: Record<RoleGroupKey, UserRole[]> = {
      ADMIN: [],
      PRACTICE: [],
      AGENCY: [],
      MANUFACTURER: [],
    };
    for (const r of availableUserRoles) {
      map[groupKeyFor(r)].push(r);
    }
    (Object.keys(map) as RoleGroupKey[]).forEach((k) =>
      map[k].sort(sortWithinGroup)
    );
    return map;
  }, [availableUserRoles]);

  useEffect(() => {
    if (!activeRole) {
      for (const key of [
        "ADMIN",
        "PRACTICE",
        "AGENCY",
        "MANUFACTURER",
      ] as RoleGroupKey[]) {
        const arr = groups[key];
        if (arr && arr.length) {
          setActiveRole(arr[0]);
          break;
        }
      }
    }
  }, [groups, activeRole]);

  useEffect(() => {
    if (activeRole?.userRoleId != null) {
      const idAsNumber = Number(activeRole.userRoleId);
      if (!Number.isNaN(idAsNumber)) {
        dispatch(fetchUserPermissions(idAsNumber));
      }
    }
  }, [dispatch, activeRole]);

  useEffect(() => {
    setLocalPermissions(permissionsByUserType || []);
  }, [permissionsByUserType]);

  const handleToggle = (sectionName: string, code: string) => {
    setLocalPermissions((prev) => {
      const next = prev.map((section) =>
        section.tokenName === sectionName
          ? {
              ...section,
              items: section.items.map((item: PermissionItem) =>
                item.code === code ? { ...item, enabled: !item.enabled } : item
              ),
            }
          : section
      );

      const nowChangeTokenValue = !arePermissionsEqual(
        next,
        originalPermissions
      );
      setIsChangeTokenValue(nowChangeTokenValue);

      return next;
    });
  };

  const handleToggleAllInSection = (sectionName: string, value: boolean) => {
    setLocalPermissions((prev) => {
      const next = prev.map((section) =>
        section.tokenName === sectionName
          ? {
              ...section,
              items: (section.items ?? []).map((it) => ({
                ...it,
                enabled: value,
              })),
            }
          : section
      );
      setIsChangeTokenValue(!arePermissionsEqual(next, originalPermissions));
      return next;
    });
  };

  const handleToggleAllEverywhere = (value: boolean) => {
    setLocalPermissions((prev) => {
      const next = prev.map((section) => ({
        ...section,
        items: (section.items ?? []).map((it) => ({ ...it, enabled: value })),
      }));
      setIsChangeTokenValue(!arePermissionsEqual(next, originalPermissions));
      return next;
    });
  };

  const handleSave = async () => {
    if (!activeRole?.userRoleId) return;

    const sections = (localPermissions ?? [])
      .filter((section) => section.items?.length)
      .map((section) => ({
        userRoleId: Number(activeRole.userRoleId),
        tokenName: section.tokenName,
        header: section.header,
        description: section.description,
        items: section.items,
      }));

    if (!sections.length) return;

    try {
      await dispatch(saveUserPermissions(sections)).unwrap();
      const successObj: SingleAlertInfo = {
        message: "User permissions Updated successfully.",
        alertType: "success",
      };
      dispatch(setSingleAlertObj(successObj));

      await dispatch(fetchUserPermissions(Number(activeRole.userRoleId)));
    } catch (err: any) {
      const errorObj: SingleAlertInfo = {
        message: "Failed to update user permissions.",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(errorObj));
    }
  };

  useEffect(() => {
    const perms = permissionsByUserType || [];
    setLocalPermissions(perms);
    setOriginalPermissions(perms);
    setIsChangeTokenValue(false);
  }, [permissionsByUserType]);

  const arePermissionsEqual = (
    a: PermissionSectionDTO[],
    b: PermissionSectionDTO[]
  ) => {
    return JSON.stringify(a) === JSON.stringify(b);
  };

  return (
    <Box sx={{ m: { xs: "16px", md: "20px" } }}>
      <Header title="User Role Management" />

      {isMobile && (
        <Box sx={{ position: "sticky", top: 90, zIndex: 2 }}>
          <Button
            onClick={() => setRoleDrawerOpen(true)}
            color="info"
            fullWidth
            startIcon={<MenuOutlined />}
            sx={{ borderRadius: 2 }}
          >
            User Role: {activeRole?.roleName ?? "Select a role"}
          </Button>
        </Box>
      )}

      <Box
        sx={{
          mt: 2,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "280px 1fr" },
          height: { md: "calc(100vh - 170px)" },
          border: (t) => `1px solid ${t.palette.divider}`,
          borderRadius: "8px",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        {!isMobile && (
          <Box
            className="custom-scroll"
            sx={{
              borderRight: (t) => `1px solid ${t.palette.divider}`,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                flexShrink: 0,
              }}
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                color={colors.grey[200]}
              >
                User Role
              </Typography>
            </Box>

            <List
              dense
              disablePadding
              sx={{ flex: 1, overflowY: "auto", height: "100%", minHeight: 0 }}
            >
              {(
                [
                  "ADMIN",
                  "PRACTICE",
                  "AGENCY",
                  "MANUFACTURER",
                ] as RoleGroupKey[]
              ).map((key) => {
                const roles = groups[key];
                if (!roles || roles.length === 0) return null;

                return (
                  <Box key={key} sx={{ pb: 1 }}>
                    <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                      <Typography
                        color={colors.info[200]}
                        variant="overline"
                        sx={{ letterSpacing: 0.6, opacity: 0.8 }}
                      >
                        {groupLabels[key]}
                      </Typography>
                    </Box>

                    {roles.map((role) => (
                      <ListItemButton
                        key={role.userRoleId}
                        selected={activeRole?.userRoleId === role.userRoleId}
                        onClick={() => setActiveRole(role)}
                        sx={{
                          px: 2,
                          py: 1.25,
                          "&.Mui-selected": {
                            bgcolor: "action.selected",
                            borderLeft: `5px solid ${colors.info[300]}`,
                            color: colors.info[300],
                          },
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{
                            sx: { fontSize: "0.95rem", fontWeight: 500 },
                          }}
                          primary={role.roleDisplayValue}
                        />
                      </ListItemButton>
                    ))}

                    <Divider sx={{ mt: 1 }} />
                  </Box>
                );
              })}

              {availableUserRoles.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No manageable roles available.
                  </Typography>
                </Box>
              )}
            </List>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Box
            sx={{
              p: 2,
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h5" fontWeight="bold" color={colors.grey[200]}>
              Permissions for: {activeRole?.roleDisplayValue ?? "—"}
            </Typography>

            {canAccessUserPermissionEdit && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleToggleAllEverywhere(true)}
                >
                  Turn All On
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleToggleAllEverywhere(false)}
                >
                  Turn All Off
                </Button>
              </Box>
            )}
          </Box>

          <Box
            className="custom-scroll"
            sx={{
              flex: 1,
              overflowY: "auto",
              height: "100%",
              minHeight: 0,
              p: 2,
              pr: 1,
            }}
          >
            {fetchPermissionsByUserTypeStatus === "loading" && (
              <Typography>Loading permissions...</Typography>
            )}

            {fetchPermissionsByUserTypeStatus === "error" && (
              <Typography color="error">Failed to load permissions</Typography>
            )}

            {fetchPermissionsByUserTypeStatus === "success" &&
              localPermissions
                .filter(
                  (section) =>
                    !(
                      section.items.length === 1 &&
                      section.items[0].label === "" &&
                      !section.items[0].enabled
                    )
                )
                // .filter((section) =>
                //   canAccessUserPermissionEdit
                //     ? true
                //     : section.items.some((item) => item.enabled)
                // )
                .map((section, index) => (
                  <PermissionSection
                    key={section.tokenName}
                    section={{
                      ...section,
                      header: `${index + 1}. ${section.header}`,
                    }}
                    toggleHandler={handleToggle}
                    colors={colors}
                    colorKey="info"
                    canEdit={canAccessUserPermissionEdit}
                    onToggleAll={handleToggleAllInSection}
                  />
                ))}

            {fetchPermissionsByUserTypeStatus === "success" &&
              localPermissions.length === 0 && (
                <Typography color="text.secondary">
                  No permissions configured for this role.
                </Typography>
              )}
          </Box>
          {canAccessUserPermissionEdit && (
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                left: 0,
                right: 0,
                borderTop: (t) => `1px solid ${t.palette.divider}`,
                alignItems: "center",
                p: 1,
                mb: { xs: 2, md: 0 },
              }}
            >
              <Grid container spacing={2} justifyContent="flex-end">
                <Grid item md={"auto"} xs={12}>
                  <Button
                    color="success"
                    variant="contained"
                    onClick={handleSave}
                    fullWidth
                    startIcon={<SaveOutlined />}
                    disabled={
                      !canAccessUserPermissionEdit || !isChangeTokenValue
                    }
                  >
                    Save Changes
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Box>
      <Drawer
        anchor="left"
        open={roleDrawerOpen}
        onClose={() => setRoleDrawerOpen(false)}
        PaperProps={{ sx: { width: "92vw", maxWidth: 360 } }}
      >
        <Toolbar sx={{ px: 2, gap: 1 }}>
          <IconButton onClick={() => setRoleDrawerOpen(false)} size="small">
            <Close />
          </IconButton>
          <Typography variant="h6" fontWeight="bold">
            User Role
          </Typography>
        </Toolbar>
        <Divider />

        <List dense disablePadding>
          {(
            ["ADMIN", "PRACTICE", "AGENCY", "MANUFACTURER"] as RoleGroupKey[]
          ).map((key) => {
            const roles = groups[key];
            if (!roles || roles.length === 0) return null;

            return (
              <Box key={key} sx={{ pb: 1 }}>
                <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                  <Typography
                    variant="overline"
                    sx={{ letterSpacing: 0.6, opacity: 0.8 }}
                  >
                    {groupLabels[key]}
                  </Typography>
                </Box>

                {roles.map((role) => (
                  <ListItemButton
                    key={role.userRoleId}
                    selected={activeRole?.userRoleId === role.userRoleId}
                    onClick={() => {
                      setActiveRole(role);
                      setRoleDrawerOpen(false);
                    }}
                    sx={{
                      px: 2,
                      py: 1.25,
                      "&.Mui-selected": {
                        bgcolor: "action.selected",
                        borderLeft: (t) => `5px solid ${colors.info[300]}`,
                      },
                    }}
                  >
                    <ListItemText
                      primaryTypographyProps={{
                        sx: { fontSize: "0.95rem", fontWeight: 500 },
                      }}
                      primary={role.roleDisplayValue}
                    />
                  </ListItemButton>
                ))}

                <Divider sx={{ mt: 1 }} />
              </Box>
            );
          })}

          {availableUserRoles.length === 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No manageable roles available.
              </Typography>
            </Box>
          )}
        </List>
      </Drawer>
    </Box>
  );
};

export default UserPermissions;
